import { useEffect, useState, type ReactNode, type ComponentType } from 'react'
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, Eye } from 'lucide-react'
import { apiGet } from '../admin/primitives.js'

/**
 * Grammaire commune des hubs métier (LOT 5).
 *
 * Un hub = hero de famille (l'état MAINTENANT, en grand) · zone attention ·
 * blocs secondaires · état de service discret · activité. Les quatre
 * familles composent ces primitives — même produit, teinte et contenu
 * propres. Données : GET /api/v1/hubs/:family (PostgreSQL uniquement).
 */

export interface HubService {
  key: string; name: string
  status: string | null
  stale: boolean
  seconds_since_success: number | null
  headline: { severity: string; title: string; detail: string | null } | null
  metrics: { key: string; label: string; value: number | string; unit: string | null; severity: string }[]
  primary_action: { label: string; url: string } | null
  never_contacted: boolean
}
export interface HubAlert {
  service_key: string; service_name: string; id: string
  severity: string; title: string; url: string | null
  last_seen_at: string; acknowledged: boolean; acknowledged_by: string | null
}
export interface HubActivity { at: string; event: string; message: string | null; actor: string | null }
export interface HubPlatform {
  version: string; migrations: number
  worker_alive: boolean; worker_heartbeat_at: string | null
  safe_mode: boolean; services_connected: number; services_fresh: number
  sessions_active: number; queued_jobs: number
}
export interface HubData {
  family: string
  generated_at: string
  services: HubService[]
  alerts: HubAlert[]
  alerts_active_total: number
  activity: HubActivity[]
  platform: HubPlatform | null
}

/**
 * Destinations d'administration — la distinction du produit, pas un choix
 * de bouton : les MANAGED SERVICES (applications autonomes : Main
 * Courante, Parc) s'administrent dans Paramètres → Services ; les
 * INTEGRATIONS (APIs directement consommées : Skello, UniFi Access, futurs
 * connecteurs Communication) dans Paramètres → Intégrations.
 */
export const HUB_ADMIN_DESTINATIONS = {
  managedServices: '/settings/services',
  integrations: '/settings/integrations',
} as const

export function useHubData(family: string): HubData | null {
  const [data, setData] = useState<HubData | null>(null)
  useEffect(() => {
    let vivant = true
    const charger = () => {
      apiGet<HubData>(`/api/v1/hubs/${family}`)
        .then((d) => { if (vivant) setData(d) })
        .catch(() => undefined)
    }
    charger()
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [family])
  return data
}

export function ageCourt(s: number | null): string {
  if (s === null) return '—'
  if (s < 60) return `il y a ${s} s`
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`
  return `il y a ${Math.round(s / 3600)} h`
}

/** L'urgence du headline surcharge la teinte de famille du hero. */
export function heroMood(svc: HubService | undefined): '' | 'hub-hero--attention' | 'hub-hero--critical' {
  if (!svc || !svc.headline) return ''
  if (svc.status === 'DOWN' || svc.headline.severity === 'CRITICAL' || svc.headline.severity === 'ERROR') return 'hub-hero--critical'
  if (svc.headline.severity === 'ACTION_REQUIRED' || svc.headline.severity === 'WARNING' || svc.stale || svc.status === 'MAINTENANCE') return 'hub-hero--attention'
  return ''
}

/* ─── HERO ─────────────────────────────────────────────────────────────── */

export function HubHero(props: {
  familyLabel: string
  Icon: ComponentType<{ size?: number | string }>
  title: string
  detail?: string | null | undefined
  mood?: '' | 'hub-hero--attention' | 'hub-hero--critical' | undefined
  cta?: { label: string; url: string } | null | undefined
  ctaExternal?: boolean | undefined
  metrics?: { label: string; value: number | string; tone?: 'warn' | 'bad' | undefined }[] | undefined
  serviceLine?: ReactNode | undefined
}) {
  const { Icon } = props
  return (
    <section className={`hub-hero ${props.mood ?? ''}`}>
      <span className="hub-hero__edge" aria-hidden="true" />
      <div className="hub-hero__top">
        <span className="hub-hero__icon" aria-hidden="true"><Icon /></span>
        <div>
          <div className="hub-hero__family">{props.familyLabel}</div>
          <h1 className="hub-hero__title">{props.title}</h1>
          {props.detail ? <p className="hub-hero__detail">{props.detail}</p> : null}
        </div>
        {props.cta ? (
          props.ctaExternal === false ? (
            <a className="lc-btn lc-btn--primary lc-btn--lg hub-hero__cta" href={props.cta.url}>
              {props.cta.label}
            </a>
          ) : (
            <a className="lc-btn lc-btn--primary lc-btn--lg hub-hero__cta" href={props.cta.url} target="_blank" rel="noopener noreferrer">
              {props.cta.label} <ExternalLink size={16} aria-hidden="true" />
            </a>
          )
        ) : null}
      </div>

      {props.metrics && props.metrics.length > 0 ? (
        <div className="hub-hero__metrics">
          {props.metrics.map((m) => (
            <div key={m.label} className="hub-metric">
              <span className={`hub-metric__value${m.tone ? ` hub-metric__value--${m.tone}` : ''}`}>{m.value}</span>
              <span className="hub-metric__label">{m.label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {props.serviceLine ? <div className="hub-hero__service">{props.serviceLine}</div> : null}
    </section>
  )
}

/** Ligne d'état de service pour le pied de hero — santé ≠ gravité métier. */
export function ServiceStateLine({ svc }: { svc: HubService }) {
  if (svc.never_contacted) {
    return <><span className="lc-dot" /> {svc.name} · aucune donnée fiable disponible — Gestion réessaie automatiquement</>
  }
  if (svc.status === 'DOWN') {
    return <><span className="lc-dot lc-dot--error" /> {svc.name} indisponible · Gestion conserve les dernières données fiables ({ageCourt(svc.seconds_since_success)})</>
  }
  if (svc.stale) {
    return <><Clock size={13} aria-hidden="true" /> Données périmées · dernières données fiables {ageCourt(svc.seconds_since_success)} · Source : {svc.name}</>
  }
  if (svc.status === 'MAINTENANCE') {
    return <><span className="lc-dot lc-dot--warning" /> {svc.name} en maintenance · données {ageCourt(svc.seconds_since_success)}</>
  }
  return <><span className="lc-dot lc-dot--ok lc-dot--live" /> Service opérationnel · données actualisées {ageCourt(svc.seconds_since_success)} · Source : {svc.name}</>
}

/* ─── ATTENTION (alertes de la famille) ────────────────────────────────── */

function sevTint(s: string): string {
  if (s === 'CRITICAL' || s === 'ERROR') return 'critical'
  if (s === 'ACTION_REQUIRED') return 'action'
  return 'info'
}

export function AttentionSection(props: {
  alerts: HubAlert[]
  total: number
  allLink: string
  Icon: ComponentType<{ size?: number | string }>
  emptyText?: string | undefined
}) {
  const { Icon } = props
  if (props.alerts.length === 0) {
    return (
      <p className="gd-calm">
        <CheckCircle2 size={15} aria-hidden="true" />
        {props.emptyText ?? 'Rien à traiter dans cette famille.'}
      </p>
    )
  }
  return (
    <section className="lc-panel hub-section" aria-label="À traiter">
      <header className="lc-panel__head">
        <h2 className="lc-section-title">À traiter</h2>
        <span className="lc-panel__spacer" />
        <a className="hub-section__link" href={props.allLink}>
          Voir toutes les alertes ({props.total}) →
        </a>
      </header>
      <div className="lc-panel__body lc-panel__body--flush">
        {props.alerts.map((a) => (
          <div key={`${a.service_key}:${a.id}`} className={`lc-action-row lc-action-row--${sevTint(a.severity)}`}>
            <span className="lc-action-row__icon" aria-hidden="true">
              {a.severity === 'CRITICAL' || a.severity === 'ERROR' ? <AlertTriangle size={20} /> : <Icon size={20} />}
            </span>
            <div className="lc-action-row__body">
              <div className="lc-action-row__title">{a.title}</div>
              <div className="lc-action-row__meta">
                {a.service_name}
                {a.acknowledged ? <span className="hub-ack"> · <Eye size={11} aria-hidden="true" /> vue{a.acknowledged_by ? ` par ${a.acknowledged_by}` : ''}</span> : null}
              </div>
            </div>
            {a.url ? (
              <a className="lc-btn" href={a.url} target="_blank" rel="noopener noreferrer">
                Traiter <ExternalLink size={14} aria-hidden="true" />
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}

/* ─── ACTIVITÉ ─────────────────────────────────────────────────────────── */

const ACTIVITY_LABELS: Record<string, string> = {
  ALERT_ACKNOWLEDGED: 'Alerte acquittée',
  SERVICE_MANUAL_REFRESH: 'Rafraîchissement manuel',
  SERVICE_ENABLED: 'Service réactivé',
  SERVICE_DISABLED: 'Service désactivé',
  SERVICE_SECRET_CHANGED: 'Credential remplacé',
  SERVICE_CONFIGURED: 'Service configuré',
}

export function ActivitySection(props: { activity: HubActivity[]; emptyText: string }) {
  return (
    <section className="lc-panel" aria-label="Activité récente">
      <header className="lc-panel__head"><h2 className="lc-section-title">Activité récente</h2></header>
      <div className="lc-panel__body lc-panel__body--flush">
        {props.activity.length === 0 ? (
          <div className="hub-activity__row"><span className="hub-activity__msg">{props.emptyText}</span></div>
        ) : props.activity.map((e, i) => (
          <div key={i} className="hub-activity__row">
            <span className="hub-activity__time">
              {new Date(e.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="hub-activity__msg">
              {e.message ?? ACTIVITY_LABELS[e.event] ?? e.event}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
